// EnergyMatchTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Zap } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  AstriaMexicoFieldBlock,
  AstriaMexicoActionList,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import {
  MXPersonFields,
  MXSubmitButton,
  PersonDetails,
  EMPTY_PERSON_DETAILS,
} from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoEnergyMatchData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Energy Match";

const buildMessage = (self: PersonDetails, partner: PersonDetails) => `
Lee nuestro Energy Match (una lectura cálida y grounded de la conexión entre nosotros).
Mi fecha de nacimiento: ${self.dob.trim()}.
Mi hora de nacimiento: ${self.time.trim() || "No proporcionada"}.
Mi ciudad de nacimiento: ${self.place.trim() || "No proporcionada"}.
Fecha de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.dob.trim()}.
Hora de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.time.trim() || "No proporcionada"}.
Ciudad de nacimiento de ${partner.name.trim() || "mi pareja"}: ${partner.place.trim() || "No proporcionada"}.
Por favor dame la lectura cálida y grounded de nuestra conexión.
`.trim();

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoEnergyMatchData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { t } = useTranslation();
  const { colors } = useMexicoColors();

  if (data) {
    return (
      <>
        <AstriaMexicoResultCard
          icon={<Zap className="w-5 h-5" />}
          title={t("mx.tabs.energyMatch")}
          subtitle={t("mx.energyMatch.subtitle")}
          shareText={`${data.opening}\n\n${data.current_energy}\n\n${data.where_this_can_go}`}
        >
          <AstriaMexicoFieldBlock label={t("mx.field.opening")} value={data.opening} />
          <AstriaMexicoFieldBlock label={t("mx.field.currentEnergy")} value={data.current_energy} />
          <AstriaMexicoFieldBlock label={t("mx.field.connectionPattern")} value={data.connection_pattern} />
          <AstriaMexicoFieldBlock label={t("mx.field.frictions")} value={data.gap_analysis} />
          <AstriaMexicoActionList label={t("mx.field.today")} items={data.heart_action_plan?.today || []} />
          <AstriaMexicoActionList
            label={t("mx.field.thisWeek")}
            items={data.heart_action_plan?.this_week || []}
          />
          <AstriaMexicoFieldBlock label={t("mx.field.whereThisCanGo")} value={data.where_this_can_go} />
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          {t("mx.common.startNewReading")}
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaMexicoResultCard icon={<Zap className="w-5 h-5" />} title={t("mx.tabs.energyMatch")}>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  self: PersonDetails;
  setSelf: (v: PersonDetails) => void;
  partner: PersonDetails;
  setPartner: (v: PersonDetails) => void;
  onSubmit: () => void;
  formValid: boolean;
}> = ({ self, setSelf, partner, setPartner, onSubmit, formValid }) => {
  const { t } = useTranslation();
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          {t("mx.tabs.energyMatch")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("mx.energyMatch.intro")}
        </p>
      </div>

      <MXPersonFields
        heading={t("mx.common.yourDetails")}
        values={self}
        onChange={setSelf}
        namePlaceholder={t("mx.common.yourName")}
      />
      <MXPersonFields heading={t("mx.common.theirDetails")} values={partner} onChange={setPartner} />

      <MXSubmitButton onClick={onSubmit} disabled={!formValid} label={t("mx.submit.energyMatch")} />
    </>
  );
};

const EnergyMatchTab: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [self, setSelf] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);
  const [partner, setPartner] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);

  const started = !!message || loading || !!error;
  const formValid = !!self.dob.trim() && !!partner.dob.trim();

  const handleSubmit = () => send(buildMessage(self, partner));

  const data = message?.astriaMexicoData as AstriaMexicoEnergyMatchData | undefined;

  if (started) {
    return (
      <AstriaMexicoShell active="energy-match">
        {loading && <MXLoadingState label={t("mx.loading.energyMatch")} />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="energy-match">
      <IntroForm
        self={self}
        setSelf={setSelf}
        partner={partner}
        setPartner={setPartner}
        onSubmit={handleSubmit}
        formValid={formValid}
      />
    </AstriaMexicoShell>
  );
};

export default EnergyMatchTab;
